import { computed, inject, nextTick, onBeforeUnmount } from "vue";
import { PAGE_DRAWER_TRANSITION_DURATION_MS } from "@modules/app/service/app.constant";
import { usePageDrawer } from "@modules/app/composables/page.drawer.ts";
import ScrollUtils from "@utils/scroll.utils";
import DeviceUtils from "@utils/device.utils";
import { isDefined, isEmpty, isNull } from "@utils/var.check";
import DOMUtils from "@utils/dom.utils";
import { ANCHOR_ADDITIONAL_OFFSET, ANNOTATION_CONTEXT_TYPE } from "../service/annotation.constant";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { AnnotationContext } from "@modules/annotation/types.ts";

export function useAnnotationAnchor() {
  const annotationContext = inject<AnnotationContext>("annotationContext");
  const { getPageDrawerExpanded, isPageDrawerExpanded, handleExpandDrawer } = usePageDrawer({
    drawerName: annotationContext?.drawerName ?? "",
  });
  const adapter = useAnnotationAttemptAdapter();
  const annotationHandler = useAnnotation(undefined, adapter);
  let tooltipPositionRefreshFrame: number | null = null;

  const getAnchorAdditionalOffset = computed(() => {
    let additionalOffset = 0;

    if (!annotationContext?.isOwnView) {
      additionalOffset += ANCHOR_ADDITIONAL_OFFSET.OTHER_VIEW_BAR;
    } else if (annotationContext.isActivityTeamResponse) {
      additionalOffset += ANCHOR_ADDITIONAL_OFFSET.TEAM_RESPONSE_BANNER;
    }

    if (annotationContext?.isAssignmentPaused()) {
      additionalOffset += ANCHOR_ADDITIONAL_OFFSET.ASSIGNMENT_PAUSE_BANNER;
    }

    additionalOffset += ANCHOR_ADDITIONAL_OFFSET.STICKY_ANNOTATION_ACCORDION_TOGGLE;
    return additionalOffset;
  });

  function cancelTooltipPositionRefresh(): void {
    if (tooltipPositionRefreshFrame === null) return;
    cancelAnimationFrame(tooltipPositionRefreshFrame);
    tooltipPositionRefreshFrame = null;
  }

  function refreshTooltipPositionDuringDrawerTransition(): void {
    cancelTooltipPositionRefresh();
    const transitionEnd = window.performance.now() + PAGE_DRAWER_TRANSITION_DURATION_MS;
    const refreshPosition = (): void => {
      window.dispatchEvent(new Event("resize"));
      if (window.performance.now() < transitionEnd) {
        tooltipPositionRefreshFrame = requestAnimationFrame(refreshPosition);
      } else {
        tooltipPositionRefreshFrame = null;
      }
    };

    tooltipPositionRefreshFrame = requestAnimationFrame(refreshPosition);
  }

  onBeforeUnmount(cancelTooltipPositionRefresh);

  function handleAnchor(id: string) {
    const wasCollapsed = !isPageDrawerExpanded.value;
    handleExpandDrawer({ drawerContentType: annotationContext?.drawerContentType ?? "" });
    if (wasCollapsed) {
      refreshTooltipPositionDuringDrawerTransition();
    }
    const selector =
      typeof id === "string" && id.includes("TOOLTIP_") ? `#${id}` : `#annotation-${id}`;
    const isEditorContext = annotationContext?.contextType === ANNOTATION_CONTEXT_TYPE.EDITOR;
    const exactSelector = `[id='${selector.slice(1)}']`;

    const getActiveAnnotationList = () => {
      const annotationLists = Array.from(document.querySelectorAll(".annotation-list"));
      return annotationLists.find((annotationList) => {
        if (!(annotationList instanceof HTMLElement)) {
          return false;
        }

        const isVisible = annotationList.offsetParent !== null;
        const isInDrawerContent = !!annotationList.closest(
          ".page-side .page-drawer .page-drawer-content",
        );

        return isVisible && isInDrawerContent;
      });
    };

    const selectAnnotationCard = (attempt = 0) => {
      const annotationList = getActiveAnnotationList();
      const annotationCard = annotationList
        ? (DOMUtils.elOf(annotationList, exactSelector)[0] as Element | undefined)
        : undefined;

      if ((isEmpty(annotationCard) || !annotationCard) && isEditorContext && attempt < 3) {
        requestAnimationFrame(() => {
          selectAnnotationCard(attempt + 1);
        });
        return;
      }

      if (isEmpty(annotationCard) || !annotationCard) return;

      annotationHandler.handleExpandAnnotationAccordion(annotationCard);

      nextTick(() => {
        if (!(annotationCard instanceof HTMLElement)) return;
        if (DeviceUtils.isMobile() || DeviceUtils.isTablet()) {
          const container = document.querySelector(".page-side .page-drawer .page-drawer-content");
          let offset = annotationCard.offsetWidth / 5;

          if (DeviceUtils.isTablet()) {
            offset += ANCHOR_ADDITIONAL_OFFSET.LEFT_ACTIVITY_NAV;
          }

          ScrollUtils.scrollLeftOfContainerWithOffset(annotationCard, container, offset);
        } else {
          const container =
            annotationList?.closest(".page-drawer") ??
            document.querySelector(".page-side .page-drawer");

          // Position the card relative to the drawer's actual viewport top. Banners/bars that
          // push the drawer down (editor view mode, other-view bar, etc.) are captured by the
          // container's own rect, so only the sticky accordion toggle needs compensating.
          container?.scrollTo({
            top:
              annotationCard.getBoundingClientRect().top -
              container.getBoundingClientRect().top +
              container.scrollTop -
              ANCHOR_ADDITIONAL_OFFSET.STICKY_ANNOTATION_ACCORDION_TOGGLE,
            left: 0,
            behavior: "smooth",
          });
        }

        const element = annotationList
          ? DOMUtils.elOf(annotationList, exactSelector)[0]
          : undefined;
        if (element) {
          element.classList.add("selected");
          return;
        }

        const fallback = annotationList
          ? DOMUtils.elOf(annotationList, `[id*='${id}']`)[0]
          : undefined;
        if (fallback) {
          fallback.classList.add("selected");
          ScrollUtils.scrollToView(`[id*='${id}']`, { inline: "center" });
        }
      });
    };

    nextTick(() => {
      selectAnnotationCard();
    });
  }

  function handleAnchorToComponentAnnotationAccordion(componentId: string, clickEvent: MouseEvent) {
    if (!isPageDrawerExpanded.value) return;

    const target = clickEvent.target as HTMLElement;

    if (
      target.tagName === "SLS-HIGHLIGHT" ||
      !isNull(DOMUtils.parent(target, ".annotation-points")) ||
      !isNull(DOMUtils.parent(target, ".student-tooltip-component"))
    ) {
      return;
    }

    if (DeviceUtils.isMobile() || DeviceUtils.isTablet()) {
      const container = document.querySelector(".page-side .page-drawer .page-drawer-content");
      const element = container?.querySelector(
        `.annotation-list #annotation-accordion-container-${componentId}`,
      );
      if (isEmpty(element)) return;

      let offset = 0;
      if (DeviceUtils.isTablet()) {
        offset += ANCHOR_ADDITIONAL_OFFSET.LEFT_ACTIVITY_NAV;
      }

      ScrollUtils.scrollLeftOfContainerWithOffset(element, container, offset);
    } else {
      const container = document.querySelector(".page-side .page-drawer");
      const element = container?.querySelector(
        `.annotation-list #annotation-accordion-container-${componentId}`,
      );
      if (!element || isEmpty(element)) return;

      container?.scrollTo({
        top:
          element.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          ANCHOR_ADDITIONAL_OFFSET.STICKY_ANNOTATION_ACCORDION_TOGGLE,
        left: 0,
        behavior: "smooth",
      });
    }
  }

  function handleExpandAnnotationAccordion(annotationCard, isActivityNote = false) {
    annotationHandler.handleExpandAnnotationAccordion(annotationCard, isActivityNote);
  }

  function isAnnotationPageDrawerExist() {
    return isDefined(getPageDrawerExpanded(annotationContext?.drawerName ?? ""));
  }

  return {
    getAnchorAdditionalOffset,
    isAnnotationPageDrawerExist,
    handleAnchor,
    handleExpandDrawer,
    handleExpandAnnotationAccordion,
    handleAnchorToComponentAnnotationAccordion,
  };
}
